import { useTitle } from "easy-page-router/react";
import { useHeaders, useSSRHook } from "ssr-hook";

import Carousel from "../components/Carousel";
import Footer from "../components/Footer";
import Icon from "@mdi/react";
import { mdiArchive, mdiCalendarPlus, mdiCalendarToday, mdiChevronLeft, mdiChevronRight, mdiMapMarker, mdiPencil, mdiPlus, mdiStar } from "@mdi/js";
import { Activity, New } from "../../types";
import { Loading, Remove } from "../components";
import moment from "moment";
import { useUser } from "../User";
import { useMemo, useState } from "react";
import { api } from "../services/api";
import EditNew, { emptyNew } from "../components/EditNew";
import Modal from "react-responsive-modal";
import EditActivity, { emptyActivity } from "../components/EditActivity";
import { getFileName, getGoogleCalendarUrl, useHead } from "../services/common";

const defaultCountOfNews = 9;
const defaultCountOfActivity = 3;

export default function Index() {
    const user = useUser();

    useHead(
        "Dění v regionu",
        "Jilemnicko - společenství obcí sdružuje 22 obcí v krásné krajině západních Krkonoš a podhůří, která nabízí bohaté možnosti pro aktivní dovolenou i poznávání historických památek. Přijeďte objevit kouzlo Jilemnicka a užijte si nezapomenutelné zážitky!"
    );

    const [news, error, isLoading, reload] = useSSRHook<New[]>("/api/news");
    const [activity, errorActivity, isActivityLoading, reloadActivity] = useSSRHook<Activity[]>("/api/activity");

    const [openNews, setOpenNews] = useState<null | New>(null);
    const [editNews, setEditNews] = useState<null | New>(null);
    const [showAllNews, setShowAllNews] = useState(false);
    const [calendar, setCalendar] = useState(moment().format());

    const calendarDays = useMemo(() => {
        const days: { day: number, isActualMonth: boolean, item: null | New }[] = [];

        const firstDay = moment(calendar).startOf("month").startOf("week");
        const lastDay = moment(calendar).endOf("month").endOf("week");

        do {
            const date = firstDay.format("YYYY-MM-DD");
            const day = firstDay.date();
            const isActualMonth = firstDay.month() === moment(calendar).month();

            let item = (news || []).find(n => {
                if (n.dateTo === null) {
                    return n.dateFrom === date;
                } else {
                    return n.dateFrom <= date && n.dateTo >= date;
                }
            }) || null;

            days.push({
                day,
                isActualMonth,
                item,
            });

            firstDay.add(1, "day");
        } while (firstDay.isBefore(lastDay));

        return days;
    }, [calendar, news]);

    const dayFrom = moment().format("YYYY-MM-DD");
    const newsFiltered = (news || []).filter(n => n.dateTo ? n.dateTo >= dayFrom : n.dateFrom >= dayFrom);

    const [editActivity, setEditActivity] = useState<null | Activity>(null);
    const [showArchivedActivity, setShowArchivedActivity] = useState(false);
    const [showAllActivity, setShowAllActivity] = useState(false);

    async function handleRemove(id: string) {
        await api.removeNews({ newsId: id });
        setOpenNews(null);
        setEditNews(null);
        reload();
    }

    async function handleRemoveActivity(id: string) {
        await api.removeActivity({ activityId: id });
        setEditActivity(null);
        reloadActivity();
    }

    const activityFiltered = (activity || []).filter(a => !a.archive);
    const activityArchived = (activity || []).filter(a => a.archive);

    const renderActivityItem = (act: Activity) => (
        <div key={act._id} className="item-activity">
            <a href={act.file.url} download={getFileName(act.title, act.file.url)}>
                <h4>{act.title}</h4>
                <div className="date">
                    <Icon path={mdiCalendarToday} size={0.7} color="#0f9759" /> {moment(act.date).format("LL")}
                </div>
                <p>{act.text} </p>
            </a>

            {user.isAdmin && <div className="admin small">
                <button onClick={() => setEditActivity(act)}>
                    <Icon path={mdiPencil} size={1} />
                </button>
                <Remove onRemove={() => handleRemoveActivity(act._id)} />
            </div>}
        </div>
    );

    return <>
        <Carousel />
        <main>
            {/* <div className="grant">
                <div className="image" />
                <p>Tento projekt byl spolufinancován z Evropského fondu pro regionální rozvoj v rámci projektu<br />
                    "Krkonošská kulturní setkání" CZ .11.4.120/0.0/0.0./15_006/0000078.
                </p>
            </div> */}
            <h1 className="hidden">Jilemnicko - společenství obcí</h1>
            <section>
                <div className="content-page">
                    <div className="star-title">
                        <Icon path={mdiStar} size={1} color="#0f9759" />
                        <span>Poslední události</span>
                        <h2>Dění v regionu</h2>

                        {user.isAdmin && <div className="admin">
                            <button onClick={() => setEditNews(emptyNew)}>
                                <Icon path={mdiPlus} size={1} />Přidat
                            </button>
                            <EditNew
                                value={editNews}
                                onClose={() => setEditNews(null)}
                                onChange={() => {
                                    setEditNews(null);
                                    setOpenNews(null);
                                    reload();
                                }}
                            />
                        </div>}
                    </div>

                    {isLoading && <Loading />}
                    {error && <p className="alert">Dění se bohužel nepodařili načíst 🙁</p>}
                    {news && <>
                        <div className="calendar-head">
                            <button onClick={() => setCalendar(moment(calendar).subtract(1, "month").format())}><Icon path={mdiChevronLeft} size={1} /></button>
                            {moment(calendar).format("MMMM YYYY")}
                            <button onClick={() => setCalendar(moment(calendar).add(1, "month").format())}><Icon path={mdiChevronRight} size={1} /></button>
                        </div>
                        <div className="calendar">
                            <div className="th">Pondělí</div>
                            <div className="th">Úterý</div>
                            <div className="th">Středa</div>
                            <div className="th">Čtvrtek</div>
                            <div className="th">Pátek</div>
                            <div className="th">Sobota</div>
                            <div className="th">Neděle</div>
                            {calendarDays.map((day, i) => <div
                                key={i}
                                className={!day.isActualMonth ? "out" : ""}
                            >
                                {day.item && <button onClick={() => setOpenNews(day.item)}>{day.day}</button>}
                                {!day.item && day.day}
                            </div>)}
                        </div>

                        {news.length === 0 && <p className="info">V následujících dnech není v tomto regionu žádné dění.</p>}

                        <div className="list-news">
                            {newsFiltered.filter((_, i) => i < defaultCountOfNews).map(n => <div key={n._id} className="item-news" onClick={() => setOpenNews(n)}>
                                <div className="top">
                                    <div className="new-image" style={{ backgroundImage: `url(${n.picture.url})` }} />
                                    <div className="date">
                                        {moment(n.dateFrom).format("LL")}
                                        {n.dateTo && ` - ${moment(n.dateTo).format("LL")}`}
                                    </div>
                                </div>
                                <div className="text">
                                    {n.place && <div className="place"><Icon path={mdiMapMarker} size={0.7} color="#0f9759" /> {n.place}</div>}
                                    <h4>{n.title}</h4>
                                </div>
                            </div>)}
                        </div>


                        {newsFiltered.length > defaultCountOfNews && <>
                            <div style={{ textAlign: "center", marginTop: "60px", marginBottom: "0px" }}>
                                <button onClick={() => setShowAllNews(!showAllNews)}>
                                    {showAllNews ? "Skrýt další dění v regionu" : "Zobrazit další dění v regionu"}
                                </button>
                            </div>
                            {showAllNews && <div className="list-news">
                                {newsFiltered.filter((_, i) => i >= defaultCountOfNews).map(n => <div key={n._id} className="item-news" onClick={() => setOpenNews(n)}>
                                    <div className="top">
                                        <div className="new-image" style={{ backgroundImage: `url(${n.picture.url})` }} />
                                        <div className="date">
                                            {moment(n.dateFrom).format("LL")}
                                            {n.dateTo && ` - ${moment(n.dateTo).format("LL")}`}
                                        </div>
                                    </div>
                                    <div className="text">
                                        {n.place && <div className="place"><Icon path={mdiMapMarker} size={0.7} color="#0f9759" /> {n.place}</div>}
                                        <h4>{n.title}</h4>
                                    </div>
                                </div>)}
                            </div>}
                        </>}
                    </>}


                    <Modal
                        open={openNews !== null}
                        onClose={() => setOpenNews(null)}
                        center
                        styles={{ modal: { width: "550px" } }}
                    >
                        {openNews && <>
                            <div className="item-news">
                                <div className="top">
                                    <div className="new-image" style={{ backgroundImage: `url(${openNews.picture.url})` }} />
                                    <div className="date">
                                        {moment(openNews.dateFrom).format("LL")}
                                        {openNews.dateTo && ` - ${moment(openNews.dateTo).format("LL")}`}
                                    </div>
                                </div>
                                <div className="text">
                                    {openNews.place && <div className="place"><Icon path={mdiMapMarker} size={0.7} color="#0f9759" /> {openNews.place}</div>}
                                    <h4>{openNews.title}</h4>
                                    <p>{openNews.text}</p>
                                    <div style={{ marginTop: "20px" }}>
                                        <a
                                            className="button small"
                                            href={getGoogleCalendarUrl(openNews)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <Icon path={mdiCalendarPlus} size={0.8} /> Přidat do Google kalendáře
                                        </a>
                                    </div>
                                </div>
                            </div>
                            {user.isAdmin && <div className="buttons">
                                <button onClick={() => setEditNews(openNews)}>
                                    <Icon path={mdiPencil} size={1} /> Upravit
                                </button>
                                <Remove onRemove={() => handleRemove(openNews._id)} >Smazat</Remove>
                            </div>}
                        </>}
                    </Modal>

                </div>
            </section>
            <section className="bg-black">
                <div className="content-page">
                    <div className="star-title">
                        <Icon path={mdiStar} size={1} color="#0f9759" />
                        <span>Informace</span>
                        <h2>Informace z činnosti</h2>

                        {user.isAdmin && <div className="admin">
                            <button onClick={() => setEditActivity(emptyActivity)}>
                                <Icon path={mdiPlus} size={1} />Přidat
                            </button>
                            <EditActivity
                                value={editActivity}
                                onClose={() => setEditActivity(null)}
                                onChange={() => {
                                    setEditActivity(null);
                                    reloadActivity();
                                }}
                            />
                        </div>}
                    </div>

                    {isActivityLoading && <Loading />}
                    {errorActivity && <p className="alert">Informace z činnosti se bohužel nepodařili načíst 🙁</p>}
                    {activity && activity.length === 0 && <p className="info">Zatím zde nejsou zde žádné informace z činnosti.</p>}
                    {activity && <>
                        <div className="list-activity">
                            {activityFiltered.filter((_, i) => i < defaultCountOfActivity).map(renderActivityItem)}
                        </div>

                        {activityFiltered.length > defaultCountOfActivity && <>
                            <div style={{ textAlign: "center", marginTop: "30px", marginBottom: "0px" }}>
                                <button onClick={() => setShowAllActivity(!showAllActivity)}>
                                    {showAllActivity ? "Skrýt další informace z činnosti" : "Zobrazit další informace z činnosti"}
                                </button>
                            </div>
                            {showAllActivity && <div className="list-activity">
                                {activityFiltered.filter((_, i) => i >= defaultCountOfActivity).map(renderActivityItem)}
                            </div>}
                        </>}

                        <div style={{ textAlign: "center", marginTop: "30px", marginBottom: "30px" }}>
                            <button onClick={() => setShowArchivedActivity(!showArchivedActivity)}>
                                {showArchivedActivity ? "Skrýt archiv" : "Zobrazit archiv"}
                            </button>
                        </div>

                        {showArchivedActivity && <>
                            <div className="star-title">
                                <Icon path={mdiArchive} size={1} color="#0f9759" />
                                <span>Archiv Informace z činnosti</span>
                            </div>
                            {activityArchived.length === 0 && <p className="info">Zatím zde nejsou zde žádné archivované informace z činnosti.</p>}
                            <div className="list-activity">
                                {activityArchived.map(renderActivityItem)}
                            </div>
                        </>}
                    </>}

                </div>
            </section>
        </main>
        <Footer />
    </>;
}
